import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { DriverActivityComponent } from '../../driver-activity/driver-activity.component';
import { DriverOperationsService, FilterService } from '../../../../../_shared/services';
import { PaginationRequest } from '../../../../../_shared/models/pagination-request.model';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { debounceTime, Observable, Subject, Subscription } from 'rxjs';
import { endOfDay, startOfDay } from 'date-fns';
import { OperationStatus } from '../../../models/operation-status.model';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-drivers-in-vehicle-modal',
  standalone: true,
  imports: [DriverActivityComponent, TranslocoPipe],
  templateUrl: './drivers-in-vehicle-modal.component.html',
  styleUrl: './drivers-in-vehicle-modal.component.scss',
})
export class DriversInVehicleModalComponent implements OnInit, OnDestroy {
  @Input() isModal: boolean = false;
  @Input() componentId: string = '';
  @Input() accounts: any[];
  @Input() search$: Observable<string>;

  @Output() toggleExpandEvent = new EventEmitter<string>();

  public title: string = '';
  public count: number = 0;
  public columnHeaders: string[] = [];
  public driversData: OperationStatus[] = [];
  public filteredDriversData: OperationStatus[] = [];
  public filterSubscription: Subscription;
  public isLoading: boolean = true;
  public searchTerm: string = '';
  public currentDate: Date = new Date();
  public isFetching: boolean = false;
  public hasError: boolean = false;
  public paginationRequest: PaginationRequest = {
    accountIds: [],
    timeFrom: startOfDay(this.currentDate),
    timeTo: endOfDay(this.currentDate),
    page: 1,
    pageSize: 15,
    searchTerm: ''
  };

  private driverOperationsService = inject(DriverOperationsService);
  private filterService = inject(FilterService);
  private errorHandlerService = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);
  private hasMoreData: boolean = true;
  private isExpanded: boolean = false;
  private cancelSubject = new Subject<void>();
  private searchSubscription: Subscription | null = null;

  ngOnInit(): void {
    this.initializeDriversValues();

    this.filterSubscription = this.filterService.filterUpdated$.subscribe((filter) => {
      if (!filter || Object.values(filter).every(x => x == null)) {
        return;
      }

      this.driversData = [];
      this.hasMoreData = true;
      this.filteredDriversData = [];
      this.hasError = false;
      this.paginationRequest.accountIds = filter.accountIds ? filter.accountIds : this.paginationRequest.accountIds;
      this.onFilterChangeAsync();
    });
    if (this.search$) {
      this.searchSubscription = this.search$.pipe(debounceTime(1000)).subscribe((searchTerm) => {
        this.isFetching = false;
        this.searchTerm = searchTerm;
        this.isLoading = true;
        this.hasError = false;
        this.filterDriversAsync(this.searchTerm);
      })
    }
  }

  private async filterDriversAsync(searchTeam: string): Promise<void> {
    this.paginationRequest.searchTerm = searchTeam ?? '';
    this.paginationRequest.page = 1;
    this.driversData = [];
    const previousCount = this.count;
    this.hasMoreData = true;
    await this.getDriverInVehicle();
    // Restore the original count after the API call
    this.count = previousCount;
    const searchLower = this.normalizeSearchTerm(this.searchTerm);
    const tokens = searchLower.split(' ').filter(Boolean);
    this.filteredDriversData = this.driversData.filter(driver => {
      const fullName = this.normalizeSearchTerm(`${driver.firstName} ${driver.lastName}`);
      return tokens.every(t => fullName.includes(t));
    });
  }

  private normalizeSearchTerm(searchTerm: string): string {
    return searchTerm.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private initializeDriversValues(): void {
    this.columnHeaders = [this.transloco.translate('driverDashboard.table.driver'), this.transloco.translate('driverDashboard.table.vehicle'), this.transloco.translate('driverDashboard.columns.timeInVehicle'), this.transloco.translate('driverDashboard.columns.location')];
  }

  protected async onFilterChangeAsync(): Promise<void> {
    await this.getDriverInVehicle();
  }

  async getDriverInVehicle() {
    if (this.isFetching || !this.hasMoreData) {
      return;
    }

    this.isLoading = true;
    this.isFetching = true;
    this.hasError = false;
    try {
      this.cancelSubject.next();
      const response = await this.driverOperationsService.getDriverInVehicleStatus(this.paginationRequest, this.cancelSubject);
      const newDrivers = response.data?.results ?? [];

      this.driversData = [...this.driversData, ...newDrivers];
      this.filteredDriversData = [...this.driversData];
      this.count = response.data?.totalCount ?? 0;
      this.hasMoreData = this.driversData.length < response.data?.totalCount!;

      this.isLoading = false;
      this.isFetching = false;
    } catch (error: any) {
      this.hasError = true;
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isLoading = false;
          this.isFetching = false;
        }
      });
    }
  }

  async onExpand(event: any): Promise<void> {
    this.isExpanded = !this.isExpanded;
    this.hasMoreData = true;
    this.isLoading = true;
    this.hasError = false;
    this.driversData = [];
    this.paginationRequest.page = 1;
    this.paginationRequest.pageSize = this.isExpanded ? 20 : 5;
    this.paginationRequest.searchTerm = '';
    this.isFetching = false;
    this.cancelSubject.next();
    this.toggleExpandEvent.emit(event);
  }

  async onScroll() {
    if (this.hasMoreData && !this.isFetching) {
      this.paginationRequest.page += 1;
      await this.getDriverInVehicle();
    }
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }

    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }

    this.cancelSubject.next();
    this.cancelSubject.complete();
  }
}